/**
 * แถบแบ่งหน้าของตาราง — ใช้ร่วมได้ทุกหน้า สไตล์อยู่ที่ .pager ใน src/index.css
 *
 * ⚠️ ต้องบอก "จาก ทั้งหมดเท่าไร" เสมอ ไม่ใช่แค่ปุ่มถัดไป/ก่อนหน้า
 *    ตารางที่ตัดข้อมูลโดยไม่บอกจำนวนรวม ทำให้คนอ่านคิดว่าที่เห็นคือทั้งหมด
 *
 * ⚠️ ไม่ใช้คลาส .toolbar กับแถบนี้ — คนละความหมาย (นั่นคือแถบตัวกรอง)
 *    แถบนี้ต้องมี justify-content: space-between และเส้นคั่นด้านบนของตัวเอง
 *
 * ⚠️ หน้าละกี่แถวเป็นกติกาของทั้งระบบ ไม่ใช่ของหน้าใครหน้ามัน — ดู backend/src/lib/paging.js
 */

const nf = new Intl.NumberFormat('th-TH')

/** ช่องว่างที่ถูกย่อ ใช้เป็น marker ในลิสต์เลขหน้า */
const GAP = '…' as const
type Item = number | typeof GAP

/**
 * เลขหน้าที่จะแสดง — โชว์หน้าแรก หน้าสุดท้าย และรอบ ๆ หน้าปัจจุบันเสมอ
 *
 * `span` = จำนวนหน้าที่โชว์ข้างละเท่าไรรอบหน้าปัจจุบัน
 * ถ้า … จะไปแทนที่หน้าเดียว ให้โชว์เลขหน้านั้นแทน (… ที่ย่อแค่หน้าเดียวเปลืองที่เท่ากัน
 * แต่กดไม่ได้ — เสียของ)
 */
export function pageItems(page: number, pages: number, span = 1): Item[] {
  if (pages <= 1) return [1]

  const items: Item[] = [1]
  const left = Math.max(2, page - span)
  const right = Math.min(pages - 1, page + span)

  if (left > 3) items.push(GAP)
  else if (left === 3) items.push(2)

  for (let i = left; i <= right; i++) items.push(i)

  if (right < pages - 2) items.push(GAP)
  else if (right === pages - 2) items.push(pages - 1)

  items.push(pages)
  return items
}

function Chevron({ double, back }: { double?: boolean; back?: boolean }) {
  // หมุนด้วย transform ทีเดียว จะได้ไม่ต้องมี path สองชุดที่วันหนึ่งแก้ไม่ตรงกัน
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={back ? { transform: 'rotate(180deg)' } : undefined}>
      {double ? (
        <>
          <path d="M7 5l7 7-7 7" />
          <path d="M14 5l7 7-7 7" />
        </>
      ) : (
        <path d="M9 5l7 7-7 7" />
      )}
    </svg>
  )
}

export function Pager({
  page,
  pageSize,
  total,
  loading,
  onPage,
  onPageSize,
  sizes = [10, 25, 50, 100],
  unit = 'รายการ',
}: {
  page: number
  pageSize: number
  total: number
  loading: boolean
  onPage: (page: number) => void
  /** ไม่ส่งมา = ไม่ให้เลือกขนาดหน้า (ตารางที่โตช้า เช่นประวัติการอัป) */
  onPageSize?: (size: number) => void
  /** ตัวเลือก "ต่อหน้า" — 10 เป็นค่าเริ่มต้นของทั้งระบบ (backend/src/lib/paging.js) */
  sizes?: number[]
  unit?: string
}) {
  const pages = Math.max(Math.ceil(total / pageSize), 1)
  const first = total === 0 ? 0 : (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, total)
  const go = (p: number) => () => onPage(Math.min(Math.max(p, 1), pages))

  return (
    <nav className="pager" aria-label="แบ่งหน้า">
      <p className="pager__count">
        {total === 0 ? (
          `ไม่มี${unit}`
        ) : (
          <>
            แสดง <strong>{nf.format(first)}–{nf.format(last)}</strong> จาก{' '}
            <strong>{nf.format(total)}</strong> {unit}
          </>
        )}
      </p>

      {/* หน้าเดียวไม่ต้องมีปุ่มอะไรให้กด — ปุ่มที่กดไม่ได้ทั้งแถบทำให้ดูเหมือนพัง */}
      {pages > 1 && (
        <div className="pager__pages">
          <button type="button" className="pager__btn" onClick={go(1)}
            disabled={loading || page <= 1} aria-label="หน้าแรก" title="หน้าแรก">
            <Chevron double back />
          </button>
          <button type="button" className="pager__btn" onClick={go(page - 1)}
            disabled={loading || page <= 1} aria-label="หน้าก่อนหน้า" title="ก่อนหน้า">
            <Chevron back />
          </button>

          {pageItems(page, pages).map((item, i) =>
            item === GAP ? (
              // key ใช้ index ได้เพราะ … ไม่มีตัวตนของตัวเอง ตำแหน่งคือทั้งหมดที่มันเป็น
              <span key={`gap${i}`} className="pager__gap" aria-hidden="true">
                {GAP}
              </span>
            ) : (
              <button key={item} type="button" className="pager__btn pager__num"
                onClick={go(item)} disabled={loading}
                aria-current={item === page ? 'page' : undefined}
                aria-label={`หน้า ${item}`}>
                {item}
              </button>
            ),
          )}

          <button type="button" className="pager__btn" onClick={go(page + 1)}
            disabled={loading || page >= pages} aria-label="หน้าถัดไป" title="ถัดไป">
            <Chevron />
          </button>
          <button type="button" className="pager__btn" onClick={go(pages)}
            disabled={loading || page >= pages} aria-label="หน้าสุดท้าย" title="หน้าสุดท้าย">
            <Chevron double />
          </button>
        </div>
      )}

      {onPageSize && (
        <label className="pager__size">
          ต่อหน้า
          <select value={pageSize} onChange={(e) => onPageSize(Number(e.target.value))} disabled={loading}>
            {sizes.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      )}
    </nav>
  )
}
